import { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { 
  Rocket, ChevronRight, ChevronDown, Folder, FolderOpen, 
  Plus, MoreVertical, Link as LinkIcon, ExternalLink, Users, Lock,
  Edit, Trash2, Share2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuTrigger,
  ContextMenuSeparator
} from '../ui/context-menu';
import { launchpadApi } from '../../lib/api';

const LaunchpadItem = forwardRef(({
  onCreateFolder,
  onEditFolder,
  onDeleteFolder,
  onShareFolder,
  onAddLink,
  onEditLink,
  onDeleteLink
}, ref) => {
  const [isExpanded, setIsExpanded] = useState(() => {
    try {
      const saved = localStorage.getItem('launchpad_expanded');
      return saved === 'true';
    } catch {
      return false;
    }
  });
  const [folders, setFolders] = useState([]);
  const [expandedFolders, setExpandedFolders] = useState(() => {
    try {
      const saved = localStorage.getItem('launchpad_expanded_folders');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [folderLinks, setFolderLinks] = useState({});
  const [loadingFolders, setLoadingFolders] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch folders on mount and when expanded
  const fetchFolders = useCallback(async () => {
    if (!isExpanded) return;
    setLoading(true);
    try {
      const response = await launchpadApi.getFolders();
      setFolders(response.data.folders || []);
    } catch (error) {
      console.error('Failed to fetch launchpad folders:', error);
    } finally {
      setLoading(false);
    }
  }, [isExpanded]);

  useEffect(() => {
    fetchFolders();
  }, [fetchFolders]);

  // Fetch folder links when folder is expanded
  const fetchFolderLinks = useCallback(async (folderId, forceRefresh = false) => {
    if (!forceRefresh && (folderLinks[folderId] || loadingFolders[folderId])) return;
    setLoadingFolders(prev => ({ ...prev, [folderId]: true }));
    try {
      const response = await launchpadApi.getFolder(folderId);
      setFolderLinks(prev => ({ ...prev, [folderId]: response.data.links || [] }));
    } catch (error) {
      console.error('Failed to fetch folder links:', error);
      setFolderLinks(prev => ({ ...prev, [folderId]: [] }));
    } finally {
      setLoadingFolders(prev => ({ ...prev, [folderId]: false }));
    }
  }, [folderLinks, loadingFolders]);

  // Toggle handlers
  const toggleLaunchpad = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    try {
      localStorage.setItem('launchpad_expanded', newState.toString());
    } catch (e) {}
  };

  const toggleFolder = (folderId) => {
    const isExpanding = !expandedFolders[folderId];
    const newState = { ...expandedFolders, [folderId]: isExpanding };
    setExpandedFolders(newState);
    try {
      localStorage.setItem('launchpad_expanded_folders', JSON.stringify(newState));
    } catch (e) {}
    if (isExpanding) {
      fetchFolderLinks(folderId);
    }
  };

  // Refresh folders
  const refreshFolders = useCallback(() => {
    setFolders([]);
    setFolderLinks({});
    fetchFolders();
  }, [fetchFolders]);

  // Refresh links for a specific folder
  const refreshFolderLinks = useCallback((folderId) => {
    if (folderId) {
      fetchFolderLinks(folderId, true);
    }
  }, [fetchFolderLinks]);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    refreshFolders,
    refreshFolderLinks
  }), [refreshFolders, refreshFolderLinks]);

  // Open link in new tab
  const handleOpenLink = (url, e) => {
    e.stopPropagation();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Get folder icon based on type
  const getFolderIcon = (folder, isExpanded) => {
    if (folder.folder_type === 'shared') {
      return isExpanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />;
    }
    return isExpanded ? <FolderOpen className="w-4 h-4" /> : <Folder className="w-4 h-4" />;
  };

  // Check if user can edit
  const canEdit = (folder) => {
    return folder.user_permission === 'owner' || folder.user_permission === 'edit';
  };

  return (
    <div className="p-3 border-t border-border">
      {/* Launchpad Header */}
      <div 
        className="flex items-center justify-between cursor-pointer group"
        onClick={toggleLaunchpad}
        data-testid="launchpad-toggle"
      >
        <div className="flex items-center gap-2">
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
          <Rocket className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Launchpad</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-[#4A9CC7]"
          onClick={(e) => {
            e.stopPropagation();
            onCreateFolder?.();
          }}
          title="Create Folder"
          data-testid="launchpad-create-folder-btn"
        >
          <Plus className="w-4 h-4" />
        </Button>
      </div>

      {/* Launchpad Content */}
      {isExpanded && (
        <div className="mt-2 space-y-1">
          {loading ? (
            <div className="text-xs text-muted-foreground px-6 py-2">Loading...</div>
          ) : folders.length === 0 ? (
            <div className="text-xs text-muted-foreground px-6 py-2">
              No folders yet. Click + to create one.
            </div>
          ) : (
            folders.map(folder => (
              <ContextMenu key={folder.launchpad_folder_id}>
                <ContextMenuTrigger>
                  <div className="group">
                    {/* Folder Row */}
                    <div 
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-muted cursor-pointer"
                      onClick={() => toggleFolder(folder.launchpad_folder_id)}
                      data-testid={`launchpad-folder-${folder.launchpad_folder_id}`}
                    >
                      <ChevronRight 
                        className={`w-3 h-3 text-muted-foreground transition-transform ${
                          expandedFolders[folder.launchpad_folder_id] ? 'rotate-90' : ''
                        }`} 
                      />
                      <span style={{ color: folder.color || '#6B7280' }}>
                        {getFolderIcon(folder, expandedFolders[folder.launchpad_folder_id])}
                      </span>
                      <span className="text-[11px] font-medium truncate flex-1">{folder.name}</span>
                      
                      {/* Folder type indicator */}
                      {folder.folder_type === 'shared' && (
                        <Users className="w-3 h-3 text-muted-foreground" />
                      )}
                      {folder.folder_type === 'personal' && folder.user_permission === 'owner' && (
                        <Lock className="w-3 h-3 text-muted-foreground opacity-50" />
                      )}
                      
                      {/* Actions Dropdown */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-5 w-5 p-0 opacity-0 group-hover:opacity-100"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-40">
                          {canEdit(folder) && (
                            <DropdownMenuItem onClick={() => onAddLink?.(folder)}>
                              <LinkIcon className="w-4 h-4 mr-2" />
                              Add Link
                            </DropdownMenuItem>
                          )}
                          {folder.user_permission === 'owner' && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => onEditFolder?.(folder)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit Folder
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => onShareFolder?.(folder)}>
                                <Share2 className="w-4 h-4 mr-2" />
                                Share
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                className="text-red-600"
                                onClick={() => onDeleteFolder?.(folder)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    {/* Links inside folder */}
                    {expandedFolders[folder.launchpad_folder_id] && (
                      <div className="ml-6 space-y-0.5">
                        {loadingFolders[folder.launchpad_folder_id] ? (
                          <div className="text-xs text-muted-foreground px-2 py-1">Loading...</div>
                        ) : (folderLinks[folder.launchpad_folder_id] || []).length === 0 ? (
                          <div className="text-xs text-muted-foreground px-2 py-1">No links yet</div>
                        ) : (
                          (folderLinks[folder.launchpad_folder_id] || []).map(link => (
                            <TooltipProvider key={link.link_id}>
                              <Tooltip delayDuration={500}>
                                <TooltipTrigger asChild>
                                  <div 
                                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-muted cursor-pointer group/link"
                                    onClick={(e) => handleOpenLink(link.url, e)}
                                    data-testid={`launchpad-link-${link.link_id}`}
                                  >
                                    {link.favicon_url ? (
                                      <img 
                                        src={link.favicon_url} 
                                        alt="" 
                                        className="w-3 h-3 rounded"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'block';
                                        }}
                                      />
                                    ) : null}
                                    <LinkIcon 
                                      className="w-3 h-3 text-muted-foreground" 
                                      style={{ display: link.favicon_url ? 'none' : 'block' }}
                                    />
                                    <span className="text-[10px] truncate flex-1">{link.title}</span>
                                    <ExternalLink className="w-2.5 h-2.5 text-muted-foreground opacity-0 group-hover/link:opacity-100" />
                                    
                                    {/* Link actions */}
                                    {canEdit(folder) && (
                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-4 w-4 p-0 opacity-0 group-hover/link:opacity-100"
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            <MoreVertical className="w-3 h-3" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-32">
                                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEditLink?.(link, folder); }}>
                                            <Edit className="w-4 h-4 mr-2" />
                                            Edit
                                          </DropdownMenuItem>
                                          <DropdownMenuItem 
                                            className="text-red-600"
                                            onClick={(e) => { e.stopPropagation(); onDeleteLink?.(link, folder); }}
                                          >
                                            <Trash2 className="w-4 h-4 mr-2" />
                                            Delete
                                          </DropdownMenuItem>
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    )}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs">
                                  <div>
                                    <p className="font-medium">{link.title}</p>
                                    <p className="text-xs text-muted-foreground break-all">{link.url}</p>
                                    {link.description && (
                                      <p className="text-xs mt-1">{link.description}</p>
                                    )}
                                  </div>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          ))
                        )}
                        
                        {/* Add link button */}
                        {canEdit(folder) && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-full justify-start h-7 px-2 text-xs text-muted-foreground hover:text-[#4A9CC7]"
                            onClick={(e) => {
                              e.stopPropagation();
                              onAddLink?.(folder);
                            }}
                          >
                            <Plus className="w-3 h-3 mr-1" />
                            Add Link
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                </ContextMenuTrigger>
                
                {/* Context Menu */}
                <ContextMenuContent className="w-40">
                  {canEdit(folder) && (
                    <ContextMenuItem onClick={() => onAddLink?.(folder)}>
                      <LinkIcon className="w-4 h-4 mr-2" />
                      Add Link
                    </ContextMenuItem>
                  )}
                  {folder.user_permission === 'owner' && (
                    <>
                      <ContextMenuSeparator />
                      <ContextMenuItem onClick={() => onEditFolder?.(folder)}>
                        <Edit className="w-4 h-4 mr-2" />
                        Edit Folder
                      </ContextMenuItem>
                      <ContextMenuItem onClick={() => onShareFolder?.(folder)}>
                        <Share2 className="w-4 h-4 mr-2" />
                        Share
                      </ContextMenuItem>
                      <ContextMenuSeparator />
                      <ContextMenuItem 
                        className="text-red-600"
                        onClick={() => onDeleteFolder?.(folder)}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete
                      </ContextMenuItem>
                    </>
                  )}
                </ContextMenuContent>
              </ContextMenu>
            ))
          )}
        </div>
      )}
    </div>
  );
});

LaunchpadItem.displayName = 'LaunchpadItem';

export default LaunchpadItem;
